/**
 * 起動〜ワールド表示までの区間計測。
 * DevTools の User Timing（`mapeditor:boot:*`）とメニュー／F3／console に出す。
 */

export type BootKind = 'reload' | 'reconnect';

export interface BootPhase {
  name: string;
  ms: number;
}

export interface BootReport {
  kind: BootKind;
  /** navigation start（再接続時は connect 開始）から初回描画まで */
  totalMs: number;
  /** init_state の JSON 解析。この間はメインスレッドが止まりクリックできない */
  blockedMs: number;
  worldReadyMs: number;
  firstFrameMs: number;
  sceneMs: number;
  uiMs: number;
  wsWaitMs: number;
  initWaitMs: number;
  parseMs: number;
  parseBytes: number;
  phases: BootPhase[];
}

const PREFIX = 'mapeditor:boot';

export class BootSession {
  readonly startedAt: number;
  private reported = false;
  private moduleMs = 0;
  private sceneMs = 0;
  private uiMs = 0;
  private connectStartedAt: number | null = null;
  private wsOpenedAt: number | null = null;
  private initReceivedAt: number | null = null;
  private worldReadyAt: number | null = null;
  private parseMs = 0;
  private parseBytes = 0;

  constructor(readonly kind: BootKind) {
    this.startedAt = kind === 'reload' ? 0 : performance.now();
    mark(`${this.kind}:start`);
  }

  recordModule(ms: number): void {
    this.moduleMs = ms;
    mark('module');
  }

  recordScene(ms: number): void {
    this.sceneMs = ms;
    mark('scene');
  }

  recordUi(ms: number): void {
    this.uiMs = ms;
    mark('ui');
  }

  recordConnect(): void {
    this.connectStartedAt = performance.now();
    mark('connect');
  }

  recordWsOpen(): void {
    if (this.wsOpenedAt !== null) return;
    this.wsOpenedAt = performance.now();
    mark('ws-open');
  }

  recordInitMessage(parseMs: number, parseBytes: number): void {
    this.initReceivedAt = performance.now();
    this.parseMs = parseMs;
    this.parseBytes = parseBytes;
    mark('init-state');
  }

  recordWorldReady(): void {
    this.worldReadyAt = performance.now();
    mark('world-ready');
  }

  get isPending(): boolean {
    return !this.reported && this.worldReadyAt !== null;
  }

  finish(): BootReport | null {
    if (this.reported || this.worldReadyAt === null) return null;
    this.reported = true;
    const firstFrameAt = performance.now();
    mark('first-terrain-frame');

    const wsOpenedAt = this.wsOpenedAt ?? this.worldReadyAt;
    const initReceivedAt = this.initReceivedAt ?? this.worldReadyAt;
    const connectStartedAt = this.connectStartedAt ?? wsOpenedAt;
    const phases: BootPhase[] = [
      { name: 'モジュール読込', ms: this.moduleMs },
      { name: 'シーン初期化', ms: this.sceneMs },
      { name: 'UI', ms: this.uiMs },
      { name: 'WebSocket待ち', ms: Math.max(0, wsOpenedAt - connectStartedAt) },
      { name: 'init_state待ち', ms: Math.max(0, initReceivedAt - wsOpenedAt) },
      { name: 'JSON解析', ms: this.parseMs },
      { name: '初回描画', ms: firstFrameAt - this.worldReadyAt },
    ];

    measure('blocked', 'init-state', 'world-ready');
    measure('to-terrain', `${this.kind}:start`, 'first-terrain-frame');

    return {
      kind: this.kind,
      totalMs: firstFrameAt - this.startedAt,
      blockedMs: this.parseMs,
      worldReadyMs: this.worldReadyAt - this.startedAt,
      firstFrameMs: firstFrameAt - this.startedAt,
      sceneMs: this.sceneMs,
      uiMs: this.uiMs,
      wsWaitMs: Math.max(0, wsOpenedAt - connectStartedAt),
      initWaitMs: Math.max(0, initReceivedAt - wsOpenedAt),
      parseMs: this.parseMs,
      parseBytes: this.parseBytes,
      phases,
    };
  }
}

export function formatBootMenu(report: BootReport): string {
  return `ワールド表示まで ${(report.totalMs / 1000).toFixed(2)}s（操作可能まで ${(report.blockedMs / 1000).toFixed(2)}s）`;
}

export function formatBootDebug(report: BootReport): string {
  return `起動 ${report.totalMs.toFixed(0)}ms  停止 ${report.blockedMs.toFixed(0)}ms  ${report.kind}`;
}

export function logBootReport(report: BootReport): void {
  console.info(
    `[mapeditor] boot ${report.totalMs.toFixed(0)}ms (blocked ${report.blockedMs.toFixed(0)}ms)`,
    report
  );
  console.table(report.phases.map((phase) => ({ 区間: phase.name, ms: Math.round(phase.ms) })));
}

function mark(name: string): void {
  performance.mark(`${PREFIX}:${name}`);
}

function measure(name: string, start: string, end: string): void {
  try {
    performance.measure(`${PREFIX}:${name}`, `${PREFIX}:${start}`, `${PREFIX}:${end}`);
  } catch {
    // 欠落した mark は無視（再接続で start が無い場合など）
  }
}
